import React from 'react';
import { useIntl } from 'react-intl';
import Html from 'components/html';
import BackgroundImage from 'components/background-image';
import Button from 'components/button';
import { noDigitsCurrencyFormat } from 'utils/format-number';
import type { ResponsiveImageOutput } from 'contracts';
import useNavigation from 'enhancers/use-navigation';

import Background from './assets/Banner_Desktop.jpg';
import BackgroundMobile from './assets/Banner_Mobile.jpg';
import {
  summerContestStartDate,
  summerContestEndDate,
  contestPrize,
  testId,
  isSummerContestBannerActive,
} from './summer-contest-banner.settings';
import messages from './summer-contest-banner.i18n';
import styles from './summer-contest-banner.module.scss';

const SummerContestBanner: React.FunctionComponent<unknown> = () => {
  const [navigate] = useNavigation();
  const { formatMessage, formatNumber } = useIntl();

  const translation = messages.pages.landing.summerContestBanner.livejasmin;

  const handleJoinClick = React.useCallback(() => {
    navigate.signup();
  }, [navigate]);

  if (!isSummerContestBannerActive()) {
    return null;
  }

  return (
    <Html.div className={[styles.summerContestBanner, 'p-0']} testId={testId.component}>
      <BackgroundImage
        className={[styles.backgroundImage, 'w-100 h-100']}
        src={Background as ResponsiveImageOutput}
        mobileSrc={BackgroundMobile as ResponsiveImageOutput}
      />

      <Html.div className={[styles.content, 'px-6 py-8 text-center']}>
        <Html.h2 className={[styles.title, 'mb-1']} typography={['title1', 'headline3-md']} fontWeight="bold">
          {formatMessage(translation.title.text)}
        </Html.h2>

        <Html.p className={[styles.prizeAmount, 'mb-4 mb-md-6']} fontWeight="light">
          {formatMessage(translation.prize.text, {
            prize: formatNumber(contestPrize, noDigitsCurrencyFormat),
            b: (value: React.ReactNode) => (
              <Html.b fontWeight="bold">
                {value}
                <Html.br className="d-block d-md-none" />
              </Html.b>
            ),
          })}
        </Html.p>

        <Html.p className={[styles.date, 'mb-4 mb-md-6']} typography="title1" fontWeight="bold">
          {formatMessage(translation.date.range, {
            startDate: new Date(summerContestStartDate).toLocaleString('default', { day: 'numeric', month: 'long' }),
            endDate: new Date(summerContestEndDate).toLocaleString('default', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }),
          })}
        </Html.p>

        <Html.div
          className={[styles.description, 'mb-8 mb-md-10']}
          typography={['body1', 'title1-md']}
          fontWeight="regular"
        >
          <Html.p className="m-0">{formatMessage(translation.description.line1)}</Html.p>
          <Html.p className="m-0">{formatMessage(translation.description.line2)}</Html.p>
        </Html.div>

        <Button size="xl" className="w-100 w-md-auto m-0" onClick={handleJoinClick} testId={testId.joinButton}>
          {formatMessage(translation.cta.buttonText)}
        </Button>
      </Html.div>
    </Html.div>
  );
};

export default SummerContestBanner;
